import { useEffect, useRef, useState, type ReactNode } from "react";
import FocusTrap from "focus-trap-react";
import { AnimatePresence, motion } from "framer-motion";
import { useI18N, useFooterSettings } from "common/hooks";
import { publicPrivacyUrl, publicTermsUrl } from "common/config";

import footerDataFallback from "@/components/Footer/footerData.json";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { ONBOARDING_INTRO_DELAY_MS, ONBOARDING_INTRO_TOUR } from "../constants";
import { useOnboardingIntro } from "../state/useOnboardingIntro";
import {
  trackTourPromptViewed,
  trackWelcomeViewed,
} from "@/analytics/onboardingAnalytics";
import AlbumArtwork from "./AlbumArtwork";
import OnboardingActionButton from "./OnboardingActionButton";
import { SHOW_ME_AROUND_ALBUMS, useAlbumArtwork } from "./useAlbumArtwork";

/**
 * The intro a first-time visitor meets before any question is asked
 * (`16204:290230`): "Welcome to Sociability" with the terms, then "Let's get
 * started!", then - if they ask for it - the six-page "how it works" tour.
 *
 * Treated as a cookie pop-up, per the frames' note: it opens on whatever page
 * the visitor lands on, and only after the page has had
 * `ONBOARDING_INTRO_DELAY_MS` to load behind it. Like the questions' modal it
 * has no Escape or click-outside dismissal - the terms have to be agreed to,
 * and after that every screen has an explicit way on.
 *
 * The pictures are drawings of app screens, not markup: rebuilding them would
 * put a fake, focusable-looking UI inside a real dialog. Each carries alt text
 * saying what it shows. Welcome and choice use the frames' panels exported
 * whole (`public/images/onboarding-intro`); the tour's come from the CMS's
 * "Show me around" albums, one per colour mode - @see `AlbumArtwork`.
 */

const HEADING_ID = "onboarding-intro-heading";

/** Material's "emphasized decelerate", as `OnboardingSurface` uses. */
const EASE_OUT = [0.05, 0.7, 0.1, 1] as const;

interface OnboardingIntroProps {
  /** The page behind has not settled yet (home's first reveal). */
  isPageLoading: boolean;
}

const OnboardingIntro = ({ isPageLoading }: OnboardingIntroProps) => {
  const { isOpen, isReplay, stage, tourPage } = useOnboardingIntro();
  const isFirstVisitDelayElapsed = useDelayOnce(
    isOpen && !isPageLoading,
    ONBOARDING_INTRO_DELAY_MS,
  );
  // The wait is for a first visit's page to load behind the intro. A replay
  // is a click in the menu and should answer it at once — and for anyone
  // whose intro is done the latch above has never tripped, so it would wait
  // the full delay every time.
  const isDelayElapsed = isReplay || isFirstVisitDelayElapsed;

  const card =
    stage === "welcome" ? "welcome" : tourPage !== null ? "tour" : "choice";

  // Once per card actually drawn. The tour's own pages are not tracked: the
  // plan's tour step names describe an older tour, not these six pages.
  const isShown = isOpen && isDelayElapsed;
  const lastTrackedCard = useRef<string | null>(null);
  useEffect(() => {
    if (!isShown || lastTrackedCard.current === card) return;
    lastTrackedCard.current = card;
    if (card === "welcome") trackWelcomeViewed();
    if (card === "choice") trackTourPromptViewed();
  }, [card, isShown]);

  return (
    <AnimatePresence>
      {isOpen && isDelayElapsed ? (
        <IntroLayer key="intro">
          {/* One card at a time; they differ in width, so they swap rather
              than morph. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={card}
              className="flex max-h-full max-w-full"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { duration: 0.28, ease: EASE_OUT },
              }}
              exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.14 } }}
            >
              {card === "welcome" ? <WelcomeCard /> : null}
              {card === "choice" ? <ChoiceCard /> : null}
              {card === "tour" ? <TourCard /> : null}
            </motion.div>
          </AnimatePresence>
        </IntroLayer>
      ) : null}
    </AnimatePresence>
  );
};

/**
 * True once `isReady` has been true for `delayMs`, and from then on for the
 * life of the component: the wait is for the page load, not something to
 * replay each time the intro's screen changes.
 */
function useDelayOnce(isReady: boolean, delayMs: number) {
  const [isElapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (!isReady || isElapsed) return;
    const timer = window.setTimeout(() => setElapsed(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, isElapsed, isReady]);
  return isElapsed;
}

/**
 * The dimmed page and the focus trap, persistent across the intro's screens
 * so the overlay fades in once and out once.
 */
const IntroLayer = ({ children }: { children: ReactNode }) => {
  useScrollLock(true);

  return (
    <motion.div
      role="presentation"
      className="fixed inset-0 z-[1030] flex items-center justify-center bg-surface-modal p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.2 } }}
      exit={{ opacity: 0, transition: { duration: 0.2 } }}
    >
      <FocusTrap
        focusTrapOptions={{
          clickOutsideDeactivates: false,
          escapeDeactivates: false,
          allowOutsideClick: true,
          initialFocus: `#${HEADING_ID}`,
          fallbackFocus: "#onboarding-intro-dialog",
        }}
      >
        <div
          id="onboarding-intro-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={HEADING_ID}
          tabIndex={-1}
          className="flex max-h-full max-w-full outline-none"
        >
          {children}
        </div>
      </FocusTrap>
    </motion.div>
  );
};

/**
 * The screen's heading. Takes focus whenever its text changes - each new
 * screen, each tour page - so moving on announces where the visitor now is
 * rather than leaving focus on a button that has just changed under them.
 */
const IntroHeading = ({
  className,
  children,
  focusKey,
}: {
  className: string;
  children: ReactNode;
  focusKey: string;
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [focusKey]);

  return (
    <h2
      ref={headingRef}
      id={HEADING_ID}
      tabIndex={-1}
      className={cn("text-fg-neutral-strong outline-none", className)}
    >
      {children}
    </h2>
  );
};

/** The card around the welcome and the choice: body, then a footer row. */
const IntroSheet = ({
  width,
  children,
  footer,
}: {
  width: string;
  children: ReactNode;
  footer: ReactNode;
}) => (
  <div
    className={cn(
      "flex max-h-full max-w-full flex-col overflow-hidden rounded-lg bg-container-neutral-white shadow-[0px_2px_6px_0px_rgba(7,7,8,0.12)]",
      width,
    )}
  >
    {/* `min-h-0` so the body scrolls on a short viewport instead of pushing
        the footer off the card. */}
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
      {children}
    </div>
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 px-6 pb-6 pt-2">
      {footer}
    </div>
  </div>
);

/** The pictures are drawn at 2x the frame's size, so they stay sharp. */
const IntroHero = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    className="aspect-[2/1] w-full shrink-0 bg-surface-secondary object-cover"
  />
);

const TitleWithLogo = ({ title }: { title: string }) => (
  <div className="flex items-center gap-3">
    <IntroHeading focusKey={title} className="title-l-bold">
      {title}
    </IntroHeading>
    <img
      src="/images/logo_icon.svg"
      alt=""
      aria-hidden="true"
      className="h-8 w-8 shrink-0 object-contain"
    />
  </div>
);

/** "Welcome to Sociability" (`16057:87207`). */
const WelcomeCard = () => {
  const i18n = useI18N();
  const { acceptTerms } = useOnboardingIntro();

  const linkClassName =
    "focus-blue rounded-sm text-fg-brand-subtle underline hover:text-fg-brand-strong";

  return (
    <IntroSheet
      width="w-[691px]"
      footer={
        <OnboardingActionButton
          variant="primary"
          label={i18n.get("onboardingIntroAgreeAndContinue")}
          withArrow
          onClick={acceptTerms}
        />
      }
    >
      <IntroHero
        src="/images/onboarding-intro/welcome.jpg"
        alt={i18n.get("onboardingIntroWelcomeImageAlt")}
      />
      <div className="flex flex-col gap-3">
        <TitleWithLogo title={i18n.get("onboardingIntroWelcomeTitle")} />
        <p className="body-m-medium text-fg-neutral-subtle">
          {i18n.get("onboardingIntroBody")}
        </p>
      </div>
      <p className="body-m-medium text-fg-neutral-subtle">
        {i18n.get("onboardingIntroTermsLead")}{" "}
        <a
          href={publicTermsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {i18n.get("onboardingIntroTermsOfService")}
        </a>{" "}
        {i18n.get("and")}{" "}
        <a
          href={publicPrivacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {i18n.get("onboardingIntroPrivacyPolicy")}
        </a>
        . {i18n.get("onboardingIntroTermsTail")}
      </p>
    </IntroSheet>
  );
};

/** "Let's get started!" (`16056:84168`). */
const ChoiceCard = () => {
  const i18n = useI18N();
  const { finish, startTour } = useOnboardingIntro();

  return (
    <IntroSheet
      width="w-[716px]"
      footer={
        <>
          <OnboardingActionButton
            variant="ghost"
            label={i18n.get("onboardingIntroOnMyOwn")}
            onClick={finish}
          />
          <OnboardingActionButton
            variant="primary"
            label={i18n.get("onboardingIntroShowMe")}
            withArrow
            onClick={startTour}
          />
        </>
      }
    >
      <IntroHero
        src="/images/onboarding-intro/choice.jpg"
        alt={i18n.get("onboardingIntroChoiceImageAlt")}
      />
      <div className="flex flex-col gap-3">
        <TitleWithLogo title={i18n.get("onboardingIntroChoiceTitle")} />
        <p className="body-m-medium text-fg-neutral-subtle">
          {i18n.get("onboardingIntroBody")}
        </p>
      </div>
    </IntroSheet>
  );
};

/**
 * One page of "Show me how it works" (`16048:70351` and the five after it):
 * the picture on the left, the words and controls in a 400px column on the
 * right. Below `md` the column takes the full width and the picture sits
 * above it.
 */
const TourCard = () => {
  const i18n = useI18N();
  const { tourPage, goNext, goPrevious, finish } = useOnboardingIntro();
  const { footerSettings } = useFooterSettings();
  const footerData = footerSettings || footerDataFallback;
  const artwork = useAlbumArtwork(SHOW_ME_AROUND_ALBUMS);

  const index = tourPage ?? 0;
  const page = ONBOARDING_INTRO_TOUR[index];
  const total = ONBOARDING_INTRO_TOUR.length;
  const isLast = index === total - 1;
  const title = i18n.get(page.titleKey);

  return (
    <div className="flex h-[598px] max-h-full w-[1024px] max-w-full flex-col overflow-hidden rounded-lg bg-container-neutral-white shadow-[0px_2px_6px_0px_rgba(7,7,8,0.12)] md:flex-row">
      {/* The map runs off the panel's left and bottom edges, as drawn. The
          card has a fixed height, so the panel cannot collapse while the
          picture loads. */}
      <AlbumArtwork
        lightSrc={artwork.light[index]}
        darkSrc={artwork.dark[index]}
        isLoading={artwork.isLoading}
        alt={i18n.get(page.imageAltKey)}
        className="h-[200px] w-full shrink-0 md:h-full md:w-auto md:min-w-0 md:flex-1"
        imageClassName="object-left-bottom"
        placeholderClassName="bottom-0 left-0 right-[15%] top-[12%] rounded-tr-[24px]"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto border-t border-border-token-brand-minimal p-6 md:w-[400px] md:flex-none md:border-l md:border-t-0">
        {/* `min-h-10`, the Skip button's height: the last page has no Skip,
            and without a floor the row collapsed to the bars and the page
            jumped up (design QA). */}
        <div className="flex min-h-10 items-center gap-3">
          {/* Decorative: the counter in the heading says the same in words. */}
          <div aria-hidden="true" className="flex flex-1 gap-3">
            {ONBOARDING_INTRO_TOUR.map((_, barIndex) => (
              <span
                key={barIndex}
                className={cn(
                  "h-2 flex-1 rounded-full transition-colors duration-300",
                  barIndex <= index
                    ? "bg-fg-brand-subtle"
                    : "bg-fg-neutral-minimal",
                )}
              />
            ))}
          </div>
          {isLast ? null : (
            <OnboardingActionButton
              variant="ghost"
              label={i18n.get("onboardingIntroSkip")}
              onClick={finish}
            />
          )}
        </div>

        <div className="flex flex-1 flex-col gap-3">
          <img
            src={
              page.pin === "verified"
                ? "/images/onboarding-intro/pin-verified.svg"
                : "/images/onboarding-intro/pin-unverified.svg"
            }
            alt=""
            aria-hidden="true"
            className="h-[42px] w-auto self-start"
          />
          <IntroHeading focusKey={title} className="title-m-bold">
            <span className="sr-only">
              {i18n
                .get("onboardingStepCounter")
                .replace("{current}", String(index + 1))
                .replace("{total}", String(total))}
              {": "}
            </span>
            {title}
          </IntroHeading>
          <p className="body-m-medium text-fg-neutral-subtle">
            {i18n.get(page.bodyKey)}
          </p>

          {/* The last page's "Share its accessibility with others!": photos
              and alerts are app-only, so it ends on how to get the app. */}
          {isLast ? (
            <div className="mt-3 flex flex-col items-center gap-6 rounded-lg border border-border-token-neutral-minimal p-6 text-center">
              <p className="body-base-medium text-fg-neutral-subtle">
                {i18n.get("scanTheQRCodeToDownloadTheApp")}
              </p>
              <img
                src="/images/Sociability_App_Download_Smart_Link_QR_Code.png"
                alt={i18n.get("onboardingIntroQrAlt")}
                className="h-[121px] w-[121px]"
              />
              <div className="flex flex-wrap items-center justify-center gap-3">
                {footerData.appStoreLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="focus-blue inline-flex h-10 w-[120px] shrink-0 items-center justify-center transition-opacity hover:opacity-80"
                  >
                    <img
                      src={link.image}
                      alt={i18n.t(link.ariaLabelKey)}
                      className="h-full w-full object-contain"
                    />
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3">
          <OnboardingActionButton
            variant="ghost"
            label={i18n.get("onboardingIntroPrevious")}
            onClick={goPrevious}
          />
          <OnboardingActionButton
            variant="primary"
            label={i18n.get(
              isLast ? "onboardingIntroGotIt" : "onboardingIntroNext",
            )}
            withArrow={!isLast}
            onClick={goNext}
          />
        </div>
      </div>
    </div>
  );
};

export default OnboardingIntro;
